import {
  Box,
  List as MuiList,
  ListItemButton as MuiListItemButton,
  ListItemIcon as MuiListItemIcon,
  ListItemText as MuiListItemText,
  Collapse,
} from "@mui/material";
import {
  KeyboardArrowUpOutlined,
  KeyboardArrowDownOutlined,
} from "@mui/icons-material";
import styled from "styled-components/macro";
import { BiRadioCircle } from "react-icons/bi";
import { RxDividerVertical } from "react-icons/rx";
import PerfectScrollbar from "react-perfect-scrollbar";
import { useDispatch, useSelector } from "react-redux";
import { Fragment, useEffect, useState } from "react";
import { useNavigate, useMatch, useLocation } from "react-router-dom";

import {
  setParentName,
  setSubParentName,
} from "../../../redux/slices/memberSlice";
import sidebarRoutes from "./sidebarRoutes";

const Wrapper = styled(Box)`
  flex: 1;
  max-height: 100%;
`;

const List = styled(MuiList)`
  padding: 0;
`;

// Parent button
const ListItemButton = styled(MuiListItemButton)`
  height: 50px;
  border-radius: 4px;
  margin: 2px 0;
  padding: 10px;
  background: #fff;
  color: #4d4d4f;

  &:hover {
    background: #fff;
    color: #4d4d4f;
  }

  &.Mui-selected {
    background-color: #b53089 !important;
    color: #fff !important;

    svg {
      fill: #fff !important;
      color: #fff !important;
    }

    &:hover {
      background-color: #b53089 !important;
    }
  }
`;

const ListItemIcon = styled(MuiListItemIcon)`
  min-width: 30px;
  padding-right: 20px;
`;

const ListItemText = styled(MuiListItemText)`
  .MuiListItemText-primary {
    font-size: 16px;
    font-weight: 600;
  }
`;

const SubListItemText = styled(MuiListItemText)`
  .MuiListItemText-primary {
    font-size: 14px;
    font-weight: 500;
  }
`;

// Child + single list
const CustomListItemButton = styled(MuiListItemButton)`
  height: 50px;
  border-radius: 4px;
  margin: 2px 0;
  padding: 10px;
  background: #fff;
  color: #4d4d4f;

  &:hover {
    background: #fff;
    color: #4d4d4f;
  }

  &.Mui-selected {
    background-color: #fff !important;
    color: #b53089 !important;

    svg {
      fill: #b53089 !important;
      color: #b53089 !important;
    }

    &:hover {
      background-color: #fff !important;
    }
  }
`;

export default function Menu({ setSelectedMenu }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const permittedRoutes = [];
  const { dashboardPermissions, agent } = useSelector(
    (state) => state.memberDetails
  );

  const { parentName } =
    useSelector((state) => state.memberDetails) ||
    sessionStorage.getItem("parentName");

  const { subParentName } =
    useSelector((state) => state.memberDetails) ||
    sessionStorage.getItem("subParentName");

  const [folderOpen, setFolderOpen] = useState({ state: {}, name: "" });
  const [subFolderOpen, setSubFolderOpen] = useState({
    state: {},
    name: "",
    parentName,
  });

  useEffect(() => {
    if (sessionStorage.getItem("parentName")) {
      let n = sessionStorage.getItem("parentName");
      setFolderOpen({ state: { [n]: true }, name: n });
    }

    if (sessionStorage.getItem("subParentName")) {
      let n = sessionStorage.getItem("subParentName");
      setSubFolderOpen({ state: { [n]: true }, name: n });
    }
  }, [parentName, subParentName]);

  for (let route of sidebarRoutes) {
    let childrenRoutes = [];

    if (route.children) {
      for (let childRoute of route.children) {
        let child = dashboardPermissions?.find(
          (page) => page.ID === childRoute.ID
        );

        if (child) {
          childrenRoutes.push(childRoute);
        }
      }

      if (childrenRoutes.length > 0) {
        route = { ...route, children: childrenRoutes };
        permittedRoutes.push({ ...route, children: childrenRoutes });
      } else {
        continue;
      }
    } else {
      let parent = dashboardPermissions?.find((page) => page.ID === route.ID);

      if (parent) {
        if (parent.ID === "template-list" && agent?.length <= 0) {
          continue;
        }
        permittedRoutes.push(route);
      }
    }
  }

  return (
    <Wrapper>
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <List>
          {permittedRoutes.map(({ name, icon, ID, path, children }) =>
            children ? (
              <ListFolder
                key={ID}
                name={name}
                icon={icon}
                children={children}
              />
            ) : (
              <ListItem
                setFolderOpen={setFolderOpen}
                key={ID}
                name={name}
                icon={icon}
                path={path}
              />
            )
          )}
        </List>
      </PerfectScrollbar>
    </Wrapper>
  );

  function ListFolder({ name, icon: Icon, children }) {
    return (
      <Fragment>
        <ListItemButton
          onClick={() => {
            setFolderOpen({
              state: { ...folderOpen.state, [name]: !folderOpen.state[name] },
              name: name,
            });
          }}
          selected={parentName === name}
        >
          <ListItemIcon>
            <Icon style={{ width: "24px", height: "24px" }} />
          </ListItemIcon>

          <ListItemText primary={name} />

          {name === folderOpen.name && folderOpen.state[name] ? (
            <KeyboardArrowUpOutlined />
          ) : (
            <KeyboardArrowDownOutlined />
          )}
        </ListItemButton>

        <Collapse
          in={
            folderOpen.state?.[name] ||
            (children.length > 0 &&
            !folderOpen.state?.[name] &&
            folderOpen.state.hasOwnProperty(name)
              ? false
              : children.filter((child) => child.name === subFolderOpen.name)
                  .length > 0)
          }
        >
          <List>
            {children.map(({ name: Name, ID, path, icon, subChilds }) =>
              subChilds ? (
                <SubListFolder
                  parentName={name}
                  key={ID}
                  name={Name}
                  children={subChilds}
                  icon={icon}
                />
              ) : (
                <ListItem
                  singleList={true}
                  key={ID}
                  name={Name}
                  path={path}
                  parentName={name}
                  icon={BiRadioCircle}
                />
              )
            )}
          </List>
        </Collapse>
      </Fragment>
    );
  }

  function SubListFolder({ parentName, name, icon: Icon, children }) {
    return (
      <Fragment>
        <ListItemButton
          onClick={() => {
            setSubFolderOpen({
              state: {
                ...subFolderOpen.state,
                [name]: !subFolderOpen.state?.[name],
                parentName: parentName,
              },
              name: name,
            });

            dispatch(setSubParentName(name));
          }}
          selected={subParentName === name}
        >
          <ListItemIcon>
            <Icon style={{ width: "24px", height: "24px" }} />
          </ListItemIcon>

          <ListItemText primary={name} />

          {name === subFolderOpen.name && subFolderOpen.state[name] ? (
            <KeyboardArrowUpOutlined />
          ) : (
            <KeyboardArrowDownOutlined />
          )}
        </ListItemButton>

        <Collapse
          in={subFolderOpen.state?.[name]}
          sx={{ marginInline: "10px" }}
        >
          <List>
            {children.map(({ name: Name, ID, path }) => (
              <ListItem
                parentName={parentName}
                key={ID}
                name={Name}
                path={path}
                singleList={false}
                subParentName={name}
                icon={RxDividerVertical}
              />
            ))}
          </List>
        </Collapse>
      </Fragment>
    );
  }

  function ListItem({
    name,
    icon: Icon,
    path = "",
    parentName,
    subParentName,
    singleList,
  }) {
    const match =
      useMatch(path) ||
      (path.includes("company") && location.pathname.includes("company"));

    return (
      <CustomListItemButton
        onClick={() => {
          navigate(path);
          setSelectedMenu(path);
          dispatch(setParentName(parentName));
          setFolderOpen({
            state: { [parentName]: true },
            name: parentName,
          });

          sessionStorage.setItem("parentName", parentName);
          if (singleList) {
            setSubFolderOpen({
              state: {},
              name: "",
            });
            dispatch(setSubParentName(""));
          }

          if (!singleList) {
            setSubFolderOpen({
              state: { [subParentName]: true },
              name: subParentName,
            });
            dispatch(setSubParentName(subParentName));
            sessionStorage.setItem("subParentName", subParentName);
          }
        }}
        selected={!!match}
      >
        <ListItemIcon>
          <Icon style={{ width: "26px", height: "26px" }} />
        </ListItemIcon>

        <SubListItemText primary={name} />
      </CustomListItemButton>
    );
  }
}
